// Coordinates are in a 1920×1080 design space with y pointing down; the renderer covers the canvas with it.
export const DESIGN_WIDTH = 1920;
export const DESIGN_HEIGHT = 1080;

// The motion plays forward through the keyframes and then back, so one loop is twice the keyframes' span.
export const KEYFRAME_INTERVAL = 10000;

/**
 * Poses every KEYFRAME_INTERVAL ms.
 *
 * lobeA and lobeB are the blob's two circles as [ x, y, radius ]. squircle is [ x, y, rotation in degrees, half size,
 * radius of its left corners as a fraction of the half size ]; those corners sharpen as the card turns away.
 */
export const KEYFRAMES = [
	{ lobeA: [ 80, 740, 200 ], lobeB: [ 200, 1120, 350 ], squircle: [ 1044, -140, 18, 520, 0.02 ] },
	{ lobeA: [ 190, 610, 190 ], lobeB: [ 400, 960, 300 ], squircle: [ 904, 72, 14, 520, 0.02 ] },
	{ lobeA: [ 250, 540, 200 ], lobeB: [ 540, 780, 290 ], squircle: [ 806, 294, 3, 530, 0.03 ] },
	{ lobeA: [ 300, 480, 190 ], lobeB: [ 660, 550, 290 ], squircle: [ 800, 536, -15, 470, 0.06 ] },
	{ lobeA: [ 370, 390, 215 ], lobeB: [ 790, 290, 250 ], squircle: [ 830, 784, -34, 430, 0.18 ] },
	{ lobeA: [ 420, 270, 220 ], lobeB: [ 744, 130, 280 ], squircle: [ 960, 984, -45, 420, 0.22 ] },
	{ lobeA: [ 390, 110, 210 ], lobeB: [ 710, -20, 250 ], squircle: [ 1150, 1150, -47, 440, 0.22 ] },
];

const LAST = KEYFRAMES.length - 1;

export const LOOP_DURATION = 2 * LAST * KEYFRAME_INTERVAL;

/**
 * Mirrors a keyframe index around both ends, so the spline comes to rest where the motion turns around.
 *
 * @param {number} index The keyframe index, possibly out of range by one.
 *
 * @returns {number} An index within KEYFRAMES.
 */
const mirror = ( index ) => {
	if ( index < 0 ) {
		return -index;
	}

	return index > LAST ? 2 * LAST - index : index;
};

/**
 * Interpolates between p1 and p2 on a Catmull-Rom spline.
 *
 * @param {number} p0 The value before p1.
 * @param {number} p1 The value at f = 0.
 * @param {number} p2 The value at f = 1.
 * @param {number} p3 The value after p2.
 * @param {number} f  The position between p1 and p2, from 0 to 1.
 *
 * @returns {number} The interpolated value.
 */
const catmullRom = ( p0, p1, p2, p3, f ) => 0.5 * (
	2 * p1 +
	( p2 - p0 ) * f +
	( 2 * p0 - 5 * p1 + 4 * p2 - p3 ) * f * f +
	( 3 * p1 - p0 - 3 * p2 + p3 ) * f * f * f
);

/**
 * The pose of both shapes at a point in time.
 *
 * @param {number} time The time in ms; any value, it loops every LOOP_DURATION.
 *
 * @returns {{lobeA: number[], lobeB: number[], squircle: number[]}} The pose, with the squircle's rotation in radians.
 */
export const poseAt = ( time ) => {
	const looped = ( ( time % LOOP_DURATION ) + LOOP_DURATION ) % LOOP_DURATION;
	const forward = Math.min( looped, LOOP_DURATION - looped ) / KEYFRAME_INTERVAL;
	const index = Math.min( Math.floor( forward ), LAST - 1 );
	const frames = [ index - 1, index, index + 1, index + 2 ].map( i => KEYFRAMES[ mirror( i ) ] );
	const interpolate = key => frames[ 0 ][ key ].map(
		( value, i ) => catmullRom( value, frames[ 1 ][ key ][ i ], frames[ 2 ][ key ][ i ], frames[ 3 ][ key ][ i ], forward - index ),
	);

	const squircle = interpolate( "squircle" );
	squircle[ 2 ] = squircle[ 2 ] * Math.PI / 180;

	return { lobeA: interpolate( "lobeA" ), lobeB: interpolate( "lobeB" ), squircle };
};
